import {
  CharacterStream,
  Maybe,
  RuleKind,
  State,
  onlineParser,
} from 'graphql-language-service';
import { memoize, zip } from 'lodash';
import * as monacoEditor from 'monaco-editor';

import { RootTypeNames } from 'src/lib/graphql/graphqlHelpers';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { isOperationDefinition } from './graphqlHelpers';
import {
  OperationPath,
  parsePathToOperationPath,
} from './operationPathHelpers';

export type ParseState = Omit<State, 'kind' | 'prevState'> & {
  // Aliased fields do not have their alias in the state, we will attach
  // this manually
  alias?: string | null;
  // The parser has internal state kinds that are not exposed by the package's types
  // These are useful for us because they wrap many different types of the same kind
  // and allow us to perform a "catch-all" check. This comes in handy for
  // extraction/inlining of variables in explorer.
  // We additionally override the prevState so we can check for these in any
  // any previously parsed tokens.
  kind: Maybe<RuleKind | 'Value' | 'Definition'>;
  prevState: Maybe<ParseState>;
};

interface Node {
  state: ParseState;
  range: monacoEditor.Range;
  tokens: Array<{ range: monacoEditor.Range; state: ParseState }>;
}

/**
 * Transform a token kind into an operation type
 */
function operationTypeFromKind(kind: ParseState['kind']) {
  switch (kind) {
    case 'Query':
    case 'ShortQuery':
      return 'query';
    case 'Mutation':
      return 'mutation';
    case 'Subscription':
      return 'subscription';
    default:
      throw new Error(`Unexpected operation type ${kind}`);
  }
}

export function getAncestorStates(state: ParseState): ParseState[] {
  const stateAncestors = [state];
  let currentState = state;
  while (currentState.prevState) {
    stateAncestors.push(currentState.prevState);
    currentState = currentState.prevState;
  }
  stateAncestors.reverse();
  return stateAncestors;
}

interface Rule {
  state: ParseState;
  range: monacoEditor.Range;
  tokens: Node['tokens'];
}

// We want to use a cache here over a single 'last value' since we might have
// range subsets being parsed. We want to limit the size here though so we
// aren't storing hundreds of documents in the cache.
class LRUStringCache extends Map {
  mostRecentKeys: string[] = [];

  get(key: string) {
    this.mostRecentKeys = [
      key,
      ...this.mostRecentKeys.filter((otherKey) => key !== otherKey),
    ];
    return super.get(key);
  }
  set(key: string, value: unknown) {
    this.mostRecentKeys = [
      key,
      ...this.mostRecentKeys.filter((otherKey) => key !== otherKey),
    ];
    if (this.mostRecentKeys.length > 10) {
      this.delete(this.mostRecentKeys.pop());
    }
    return super.set(key, value);
  }
}

const memoedRunOnlineParserInternal = memoize(runOnlineParserInternal);
memoedRunOnlineParserInternal.cache = new LRUStringCache();

export function runOnlineParser(
  documentText: string,
  callback: (
    state: ParseState,
    ruleRange: monacoEditor.Range,
    tokens: Node['tokens'],
  ) => void,
) {
  memoedRunOnlineParserInternal(documentText).forEach((rule) =>
    callback(rule.state, rule.range, rule.tokens),
  );
}

/**
 * This takes in a text and runs an online parser over it. This it will return
 * an array of all completed nodes
 *
 * Some notes:
 * - When an invalid state is reached the callback will be called on all open
 *   nodes as if they had been completed.
 * - Field aliases are not normally included as part of the state, but have been
 *   added to make working wit this easier.
 * - Ending punctuation is norally not included as part of a state but will be
 *   considered part of the previous state in the callbacks.
 */
function runOnlineParserInternal(documentText: string): Rule[] {
  const lines = documentText.split('\n');
  const parser = onlineParser();
  let mutableState: ParseState = { ...parser.startState() };
  let savedAncestorStates: Node[] = [];
  const allRules: Rule[] = [];

  lines.forEach((line, lineIndex) => {
    const stream = new CharacterStream(`${line}\n`);
    while (!stream.eol()) {
      const tokenType = parser.token(stream, mutableState as State);

      // Aliased fields do not have their alias in the state, instead we get
      // two different names at different steps in the rule. Attach the name
      // for the first two steps as an alias.
      if (mutableState.kind === 'AliasedField' && mutableState.step < 2) {
        mutableState.alias = mutableState.name;
      }

      const state: ParseState = { ...mutableState };
      const ancestorStates = getAncestorStates(state);

      // monaco positions
      const lineNumber = lineIndex + 1;
      const startColumn = stream.getStartOfToken() + 1;
      const endColumn = stream.getCurrentPosition() + 1;
      const tokenRange = new monacoEditor.Range(
        lineNumber,
        startColumn,
        lineNumber,
        endColumn,
      );

      const lastState = savedAncestorStates.length
        ? savedAncestorStates.slice(-1)[0]
        : undefined;

      if (
        lastState &&
        lastState.state.kind === state.kind &&
        lastState.state.step <= state.step &&
        ancestorStates.length === savedAncestorStates.length
      ) {
        // Continuing previous rule
        // For each ancestor, send an incomplete callback
        // These are non null as we checked
        // `ancestorStates.length === savedAncestorStates.length` above
        (
          zip(ancestorStates, savedAncestorStates) as [ParseState, Node][]
        ).forEach(([ancestorState, savedAncestorState]: [ParseState, Node]) => {
          // eslint-disable-next-line no-param-reassign
          savedAncestorState.state = ancestorState;
          if (tokenType !== 'ws') {
            // eslint-disable-next-line no-param-reassign
            savedAncestorState.range = savedAncestorState.range.setEndPosition(
              lineNumber,
              endColumn,
            );
          }
        });
      } else {
        // Starting a new rule
        let ancestorsToAdd: Node[] = [];
        let includePunctuation = false;
        // For each node that this is not a part of (complete nodes), call
        // the callback
        const ancestorPairs = zip(ancestorStates, savedAncestorStates);
        ancestorPairs.reverse();
        // eslint-disable-next-line no-loop-func
        ancestorPairs.forEach(([ancestorState, savedAncestorState]) => {
          // Node matched, update saved node
          if (
            ancestorState &&
            savedAncestorState &&
            savedAncestorState.state.kind === ancestorState.kind &&
            (savedAncestorState.state.step === ancestorState.step ||
              ancestorState.step > 1 ||
              // `FragmentSpread`s start parsing at step 1
              (ancestorState.step > 0 &&
                savedAncestorState.state.kind !== 'FragmentSpread'))
          ) {
            // eslint-disable-next-line no-param-reassign
            savedAncestorState.state = ancestorState;
            if (tokenType !== 'ws') {
              // eslint-disable-next-line no-param-reassign
              savedAncestorState.range =
                savedAncestorState.range.setEndPosition(lineNumber, endColumn);
            }
          } else {
            if (savedAncestorState) {
              // Update node and call the complete callback.
              // Punctuation is not included as part of the rule, add it
              // manually.
              const rule = savedAncestorState.state.rule;
              const finalRule = Array.isArray(rule) && rule.slice(-1)[0];
              if (
                includePunctuation ||
                (finalRule &&
                  typeof finalRule === 'object' &&
                  finalRule.style === 'punctuation')
              ) {
                // eslint-disable-next-line no-param-reassign
                savedAncestorState.range =
                  savedAncestorState.range.setEndPosition(
                    lineNumber,
                    startColumn + 1,
                  );
                includePunctuation = true;
              }

              allRules.push(savedAncestorState);
              savedAncestorStates.pop();
            }
            if (ancestorState) {
              const newAncestor = {
                state: ancestorState,
                range: tokenRange,
                tokens: [],
              };
              ancestorsToAdd = [newAncestor, ...ancestorsToAdd];
            }
          }
        });
        savedAncestorStates = [...savedAncestorStates, ...ancestorsToAdd];
      }

      if (tokenType !== 'ws' && savedAncestorStates.length) {
        savedAncestorStates.forEach((savedAncestorState) => {
          savedAncestorState.tokens.push({ range: tokenRange, state });
        });
      }
    }

    // If we reached an invalid state, reset to the base Document state
    if (!mutableState.kind) {
      mutableState = parser.startState();
    }
  });

  while (savedAncestorStates.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    allRules.push(savedAncestorStates.pop()!);
  }

  return allRules;
}

/**
 * Calculate the node path to a given offset in this operation string
 */
export function getPathToOffset(
  model: monacoEditor.editor.ITextModel,
  position: monacoEditor.Position,
): {
  path?: OperationPath;
  endState: ParseState;
  endRange: monacoEditor.Range;
  hoverRange: monacoEditor.Range;
} | null {
  let hoverMatch:
    | {
        state: ParseState;
        range: monacoEditor.Range;
        fullRange: monacoEditor.Range;
      }
    | undefined;
  runOnlineParser(model.getValue(), (state, range) => {
    if (range.containsPosition(position) && !hoverMatch) {
      const hover = getHoverForState(state, range, model);
      if (hover?.range.containsPosition(position)) {
        hoverMatch = {
          ...hover,
          fullRange: range,
        };
      }
    }
  });

  if (!hoverMatch) {
    return null;
  }

  // The matched node must be path of the path
  const path = parsePathToOperationPath(getAncestorStates(hoverMatch.state));

  return {
    path,
    endState: hoverMatch.state,
    endRange: hoverMatch.fullRange,
    hoverRange: hoverMatch.range,
  };
}

/**
 * For each hoverable state type, gets its hover range
 */
function getHoverForState(
  state: ParseState,
  range: monacoEditor.Range,
  model: monacoEditor.editor.ITextModel,
): { state: ParseState; range: monacoEditor.Range } | undefined {
  switch (state.kind) {
    case 'Field':
    case 'AliasedField': {
      const rangeStart = model.getOffsetAt(range.getStartPosition());
      const ruleValue = model.getValueInRange(range);
      const match = new RegExp(`(.*(?:\\W|\\s+|^))${state.name}(?:\\W|$)`).exec(
        ruleValue,
      );

      if (!isMinLengthArray(2, match)) {
        return undefined;
      }

      const index = rangeStart + match[1].length;

      return {
        state,
        range: monacoEditor.Range.fromPositions(
          model.getPositionAt(index),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          model.getPositionAt(index + state.name!.length),
        ),
      };
    }
    case 'FragmentSpread':
      return { state, range };
    case 'NamedType': {
      const parentState = state.prevState?.prevState;
      if (
        parentState?.kind === 'FragmentDefinition' ||
        parentState?.kind === 'InlineFragment'
      ) {
        return { state: parentState, range };
      }
      return { state, range };
    }
    case 'Argument':
      return { state, range };
    case 'Variable':
      return { state, range };
    case 'Directive':
      return { state, range };
    case 'Query':
    case 'Mutation':
    case 'Subscription': {
      const rangeStart = model.getOffsetAt(range.getStartPosition());
      const ruleValue = model.getValueInRange(range);
      const match = /(.*)(query|mutation|subscription)/.exec(ruleValue);
      if (!isMinLengthArray(3, match)) {
        return undefined;
      }
      const index = rangeStart + match[1].length;
      return {
        state,
        range: monacoEditor.Range.fromPositions(
          model.getPositionAt(index),
          model.getPositionAt(index + match[2].length),
        ),
      };
    }
    default:
      return undefined;
  }
}

export type GraphqlDefinition = {
  isSubscription: boolean;
  name: string | null | undefined;
  type: string;
  range: monacoEditor.Range;
  kind: 'operation' | 'fragment';
};

/**
 * Get all the operation definitions in the given document
 */
export function getDocumentDefinitions(
  document: string,
  rootTypeNames: RootTypeNames,
): {
  operationDefinitions: (GraphqlDefinition & { kind: 'operation' })[];
  fragmentDefinitions: (GraphqlDefinition & { kind: 'fragment' })[];
  commentRanges: monacoEditor.Range[];
  // TODO: separate variableOperationCount. To avoid running the online parser multiple times we
  // can make a utility that takes multiple functions to run with the online parser.
  /**
   * The Number of unique operations a variable appears in.
   */
  variableOperationCount: Record<string, number>;
} {
  const operationDefinitions: (GraphqlDefinition & {
    kind: 'operation';
  })[] = [];
  const fragmentDefinitions: (GraphqlDefinition & { kind: 'fragment' })[] = [];
  const commentRanges: monacoEditor.Range[] = [];
  const variableOperationCount: Record<string, number> = {};
  const variables = new Set<string>();
  runOnlineParser(document, (state, range) => {
    if (isOperationDefinition(state)) {
      const operationType = operationTypeFromKind(state.kind);
      const type = rootTypeNames[operationType] ?? operationType;
      operationDefinitions.push({
        kind: 'operation',
        type,
        name: state.name,
        range,
        isSubscription: operationType === 'subscription',
      });
    }

    // Starting a fragment definition
    if (state.kind === 'FragmentDefinition') {
      fragmentDefinitions.push({
        kind: 'fragment',
        name: state.name,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        type: state.type!,
        range,
        isSubscription: false,
      });
    }

    if (isOperationDefinition(state) || state.kind === 'FragmentDefinition') {
      variables.forEach((variable) => {
        variableOperationCount[variable] =
          (variableOperationCount[variable] ?? 0) + 1;
      });
      variables.clear();
    }

    if (state.kind === 'Variable' && state.name) {
      variables.add(state.name);
    }

    if (state.kind === 'Comment' && state.prevState?.kind === 'Document') {
      commentRanges.push(range);
    }
  });

  variables.forEach((variable) => {
    variableOperationCount[variable] =
      (variableOperationCount[variable] ?? 0) + 1;
  });

  return {
    operationDefinitions,
    fragmentDefinitions,
    variableOperationCount,
    commentRanges,
  };
}

/**
 * Given a single definition, find all the used fragments
 */
export function getUsedFragments(
  model: monacoEditor.editor.ITextModel,
  operationDefinition: GraphqlDefinition,
  fragmentDefinitions: GraphqlDefinition[],
): GraphqlDefinition[] {
  const fragmentNames: Set<string> = new Set([]);
  const definitions = [operationDefinition];

  while (definitions.length) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const definition = definitions.pop()!;

    runOnlineParser(model.getValue(), (state, range) => {
      if (
        state.kind === 'FragmentSpread' &&
        state.name &&
        !fragmentNames.has(state.name) &&
        definition.range.containsRange(range)
      ) {
        const fragment = fragmentDefinitions.find(
          (fragmentDefinition) => state.name === fragmentDefinition.name,
        );
        if (fragment) {
          definitions.push(fragment);
        }
        fragmentNames.add(state.name);
      }
    });
  }

  return fragmentDefinitions.filter(
    (definition) => definition.name && fragmentNames.has(definition.name),
  );
}

export function getActiveFragment(
  editorPosition: monacoEditor.Position = new monacoEditor.Position(1, 1),
  fragmentDefinitions: GraphqlDefinition[],
) {
  return (
    editorPosition &&
    fragmentDefinitions.find((definition) =>
      definition.range.containsPosition(editorPosition),
    )
  );
}

/**
 * Get inlined arguments
 */
export function getInlineArgumentRanges(document: string) {
  const argumentRanges: monacoEditor.Range[] = [];
  runOnlineParser(document, (state, range, tokens) => {
    if (
      state.kind === 'Value' &&
      !tokens.find((token) => token.state.kind === 'Variable')
    ) {
      argumentRanges.push(range);
    }
  });

  return argumentRanges;
}
